/**
 * Zenwise Cart Drawer - Standalone JavaScript
 * No dependencies on theme code
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    get freeShippingThreshold() {
      return window.zenwiseCartSettings?.freeShippingThreshold || 50.00;
    },
    get shippingQualifiedText() {
      return window.zenwiseCartSettings?.shippingQualifiedText || '🎉 You qualify for free ground shipping!';
    },
    cartDrawerId: 'ZenwiseCartDrawer',
    overlayId: 'ZenwiseCartOverlay',
    closeButtonId: 'ZenwiseCartClose',
    formId: 'ZenwiseCartForm',
    emptyCartId: 'ZenwiseCartEmpty'
  };

  // Selectors
  const SELECTORS = {
    drawer: `#${CONFIG.cartDrawerId}`,
    overlay: `#${CONFIG.overlayId}`,
    closeButton: `#${CONFIG.closeButtonId}`,
    form: `#${CONFIG.formId}`,
    emptyCart: `#${CONFIG.emptyCartId}`,
    items: '[data-zenwise-cart-items]',
    count: '[data-zenwise-cart-count]',
    subtotal: '[data-zenwise-subtotal]',
    shippingFill: '[data-zenwise-shipping-fill]',
    shippingText: '[data-zenwise-shipping-text]',
    shippingAmount: '[data-zenwise-shipping-amount]',
    recommendationsGrid: '[data-zenwise-recommendations-grid]',
    qtyMinus: '[data-zenwise-qty-minus]',
    qtyPlus: '[data-zenwise-qty-plus]',
    qtyInput: '[data-zenwise-qty-input]',
    removeBtn: '[data-zenwise-remove]',
    trigger: '.js-drawer-open-zenwise'
  };

  /**
   * Zenwise Cart Drawer Class
   */
  class ZenwiseCartDrawer {
    constructor() {
      this.drawer = document.querySelector(SELECTORS.drawer);
      if (!this.drawer) {
        console.warn('Zenwise Cart Drawer not found');
        return;
      }

      this.overlay = document.querySelector(SELECTORS.overlay);
      this.closeButton = document.querySelector(SELECTORS.closeButton);
      this.form = document.querySelector(SELECTORS.form);
      this.emptyCart = document.querySelector(SELECTORS.emptyCart);
      this.itemsContainer = document.querySelector(SELECTORS.items);
      
      this.isOpen = false;
      this.isUpdating = false;

      this.init();
    }

    init() {
      // Close button
      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => this.close());
      }

      // Overlay click
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Quantity controls
      this.setupQuantityControls();

      // Remove buttons
      this.setupRemoveButtons();

      // Trigger buttons
      this.setupTriggerButtons();

      // Listen for add to cart events
      document.addEventListener('zenwise:cart:added', (e) => {
        this.updateCart();
        this.open();
      });

      // Initial cart load
      this.updateCart();
    }

    /**
     * Open cart drawer
     */
    open() {
      if (this.isOpen) return;
      
      this.drawer.classList.add('zenwise-cart-open');
      document.body.classList.add('zenwise-cart-no-scroll');
      this.isOpen = true;

      // Dispatch event
      document.dispatchEvent(new CustomEvent('zenwise:cart:opened'));
    }

    /**
     * Close cart drawer
     */
    close() {
      if (!this.isOpen) return;
      
      this.drawer.classList.remove('zenwise-cart-open');
      document.body.classList.remove('zenwise-cart-no-scroll');
      this.isOpen = false;

      // Dispatch event
      document.dispatchEvent(new CustomEvent('zenwise:cart:closed'));
    }

    /**
     * Toggle cart drawer
     */
    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    /**
     * Setup quantity controls
     */
    setupQuantityControls() {
      // Delegate events for dynamic content
      document.addEventListener('click', (e) => {
        // Plus button
        const plusBtn = e.target.closest(SELECTORS.qtyPlus);
        if (plusBtn) {
          e.preventDefault();
          const key = plusBtn.dataset.zenwiseQtyPlus;
          this.changeQuantity(key, 1);
          return;
        }

        // Minus button
        const minusBtn = e.target.closest(SELECTORS.qtyMinus);
        if (minusBtn) {
          e.preventDefault();
          const key = minusBtn.dataset.zenwiseQtyMinus;
          this.changeQuantity(key, -1);
          return;
        }
      });

      // Input change
      document.addEventListener('change', (e) => {
        const input = e.target.closest(SELECTORS.qtyInput);
        if (input) {
          const key = input.dataset.zenwiseQtyInput;
          const newQty = Math.max(0, parseInt(input.value) || 0);
          this.updateLineItem(key, newQty);
        }
      });
    }

    /**
     * Setup remove buttons
     */
    setupRemoveButtons() {
      document.addEventListener('click', (e) => {
        const removeBtn = e.target.closest(SELECTORS.removeBtn);
        if (removeBtn) {
          e.preventDefault();
          const key = removeBtn.dataset.zenwiseRemove;
          this.removeItem(key);
        }
      });
    }

    /**
     * Setup trigger buttons
     */
    setupTriggerButtons() {
      const triggers = document.querySelectorAll(SELECTORS.trigger);
      triggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.open();
        }, true);
      });
    }

    /**
     * Change quantity by delta
     */
    changeQuantity(key, delta) {
      const input = document.querySelector(`[data-zenwise-qty-input="${key}"]`);
      if (!input) return;

      const currentQty = parseInt(input.value) || 0;
      const newQty = Math.max(0, currentQty + delta);
      
      input.value = newQty;
      this.updateLineItem(key, newQty);
    }

    /**
     * Remove item from cart
     */
    removeItem(key) {
      this.updateLineItem(key, 0);
    }

    /**
     * Update line item via Ajax
     */
    updateLineItem(key, quantity) {
      if (this.isUpdating) return;
      this.isUpdating = true;

      const data = {
        id: key,
        quantity: quantity
      };

      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(data)
      })
      .then(response => response.json())
      .then(cart => {
        this.isUpdating = false;
        this.updateCart();
        
        // Dispatch event
        document.dispatchEvent(new CustomEvent('zenwise:cart:updated', { 
          detail: { cart } 
        }));
      })
      .catch(error => {
        this.isUpdating = false;
        console.error('Error updating cart:', error);
        this.showError('Failed to update cart');
      });
    }

    /**
     * Update entire cart
     */
    updateCart() {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          this.updateCartCount(cart.item_count);
          this.updateSubtotal(cart.total_price);
          this.updateShippingProgress(cart.total_price);
          this.renderCartItems(cart);
          this.loadRecommendations();
          this.toggleEmptyState(cart.item_count === 0);
        })
        .catch(error => {
          console.error('Error fetching cart:', error);
        });
    }

    /**
     * Update cart count
     */
    updateCartCount(count) {
      const countElements = document.querySelectorAll(SELECTORS.count);
      countElements.forEach(el => {
        el.textContent = `${count} item(s)`;
      });

      // Update header cart bubble if it exists
      const bubbles = document.querySelectorAll('[data-cart-count]');
      bubbles.forEach(bubble => {
        bubble.textContent = count;
        if (count > 0) {
          bubble.style.display = '';
        } else {
          bubble.style.display = 'none';
        }
      });
    }

    /**
     * Update subtotal
     */
    updateSubtotal(totalPrice) {
      const subtotalElements = document.querySelectorAll(SELECTORS.subtotal);
      const formattedPrice = this.formatMoney(totalPrice);
      
      subtotalElements.forEach(el => {
        el.textContent = formattedPrice;
      });
    }

    /**
     * Update shipping progress
     */
    updateShippingProgress(totalPrice) {
      const priceInDollars = totalPrice / 100;
      const remaining = Math.max(0, CONFIG.freeShippingThreshold - priceInDollars);
      const progress = Math.min(100, (priceInDollars / CONFIG.freeShippingThreshold) * 100);

      const fillEl = document.querySelector(SELECTORS.shippingFill);
      const textEl = document.querySelector(SELECTORS.shippingText);
      const amountEl = document.querySelector(SELECTORS.shippingAmount);

      if (fillEl) {
        fillEl.style.width = `${progress}%`;
      }

      if (textEl && amountEl) {
        if (remaining > 0) {
          amountEl.textContent = `$${remaining.toFixed(2)}`;
          // Text after amount is set in the HTML from customizer
        } else {
          textEl.innerHTML = `<strong>${CONFIG.shippingQualifiedText}</strong>`;
        }
      }
    }

    /**
     * Render cart items
     */
    renderCartItems(cart) {
      if (!this.itemsContainer) return;

      if (cart.items.length === 0) {
        this.itemsContainer.innerHTML = '';
        return;
      }

      // Build items HTML
      let html = '';
      cart.items.forEach(item => {
        html += this.buildCartItemHTML(item);
      });

      this.itemsContainer.innerHTML = html;
    }

    /**
     * Build cart item HTML
     */
    buildCartItemHTML(item) {
      const image = item.image || '';
      const title = item.product_title || '';
      const variant = item.variant_title || '';
      const price = this.formatMoney(item.final_price);
      const originalPrice = item.original_price !== item.final_price ? this.formatMoney(item.original_price) : '';
      const quantity = item.quantity;
      const key = item.key;

      return `
        <div class="zenwise-cart-item" data-zenwise-item-key="${key}">
          <div class="zenwise-item-image">
            <a href="${item.url}" class="zenwise-item-image-link">
              <img src="${image}" alt="${title}" loading="lazy" width="80" height="80">
            </a>
          </div>
          <div class="zenwise-item-details">
            <div class="zenwise-item-info">
              <a href="${item.url}" class="zenwise-item-title">${title}</a>
              ${variant ? `<div class="zenwise-item-variant"><span class="zenwise-variant-option">${variant}</span></div>` : ''}
            </div>
            <div class="zenwise-item-pricing">
              <div class="zenwise-price-wrapper">
                ${originalPrice ? `<span class="zenwise-price zenwise-price-original">${originalPrice}</span>` : ''}
                <span class="zenwise-price ${originalPrice ? 'zenwise-price-sale' : ''}">${price}</span>
              </div>
            </div>
            <div class="zenwise-item-actions">
              <div class="zenwise-quantity">
                <button type="button" class="zenwise-qty-btn zenwise-qty-minus" data-zenwise-qty-minus="${key}" aria-label="Decrease quantity">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </button>
                <input type="number" class="zenwise-qty-input" value="${quantity}" min="0" data-zenwise-qty-input="${key}" aria-label="Quantity">
                <button type="button" class="zenwise-qty-btn zenwise-qty-plus" data-zenwise-qty-plus="${key}" aria-label="Increase quantity">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
              <button type="button" class="zenwise-item-remove" data-zenwise-remove="${key}" aria-label="Remove item">Remove</button>
            </div>
          </div>
        </div>
      `;
    }

    /**
     * Load recommendations
     */
    loadRecommendations() {
      const container = document.querySelector(SELECTORS.recommendationsGrid);
      if (!container) return;

      fetch('/recommendations/products.json?limit=4')
        .then(response => response.json())
        .then(data => {
          if (data.products && data.products.length > 0) {
            this.renderRecommendations(data.products, container);
          }
        })
        .catch(error => {
          console.error('Error loading recommendations:', error);
        });
    }

    /**
     * Render recommendations
     */
    renderRecommendations(products, container) {
      const html = products.map(product => {
        const price = this.formatMoney(product.price);
        const image = product.featured_image || '';
        const variantId = product.variants && product.variants[0] ? product.variants[0].id : '';

        return `
          <div class="zenwise-recommendation-item">
            <a href="${product.url}" class="zenwise-recommendation-image">
              <img src="${image}" alt="${product.title}" loading="lazy">
            </a>
            <a href="${product.url}" class="zenwise-recommendation-title">${product.title}</a>
            <div class="zenwise-recommendation-price">${price}</div>
            <button type="button" class="zenwise-recommendation-add" data-zenwise-add-recommendation="${variantId}">Add</button>
          </div>
        `;
      }).join('');

      container.innerHTML = html;

      // Add click handlers
      container.querySelectorAll('[data-zenwise-add-recommendation]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const variantId = e.target.dataset.zenwiseAddRecommendation;
          this.addRecommendation(variantId, e.target);
        });
      });
    }

    /**
     * Add recommendation to cart
     */
    addRecommendation(variantId, button) {
      const originalText = button.textContent;
      button.textContent = 'Adding...';
      button.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      })
      .then(response => response.json())
      .then(() => {
        button.textContent = '✓ Added';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);
        
        this.updateCart();
      })
      .catch(error => {
        console.error('Error adding recommendation:', error);
        button.textContent = 'Error';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);
      });
    }

    /**
     * Toggle empty state
     */
    toggleEmptyState(isEmpty) {
      if (!this.emptyCart || !this.form) return;

      if (isEmpty) {
        this.form.style.display = 'none';
        this.emptyCart.style.display = 'flex';
      } else {
        this.form.style.display = 'flex';
        this.emptyCart.style.display = 'none';
      }
    }

    /**
     * Format money
     */
    formatMoney(cents) {
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    }

    /**
     * Show error message
     */
    showError(message) {
      console.error(message);
      // You can implement a toast notification here
    }
  }

  // Initialize cart drawer
  let cartDrawerInstance = null;

  function initCartDrawer() {
    if (!cartDrawerInstance) {
      cartDrawerInstance = new ZenwiseCartDrawer();
    }
    return cartDrawerInstance;
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartDrawer);
  } else {
    initCartDrawer();
  }

  // Global functions
  window.openCartDrawer = function() {
    const instance = initCartDrawer();
    if (instance) {
      instance.open();
    }
  };

  window.closeCartDrawer = function() {
    const instance = initCartDrawer();
    if (instance) {
      instance.close();
    }
  };

  window.toggleCartDrawer = function() {
    const instance = initCartDrawer();
    if (instance) {
      instance.toggle();
    }
  };

  window.refreshCartDrawer = function() {
    const instance = initCartDrawer();
    if (instance) {
      instance.updateCart();
    }
  };

})();
